import React, { useMemo, useState } from "react";
import axios from "axios";
import { toast } from "react-hot-toast";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import LoginIcon from "@mui/icons-material/Login";
import PersonAddAltRoundedIcon from "@mui/icons-material/PersonAddAltRounded";

import { useStyle } from "../../context/styleContext/styleContext";

export default function LoginRegisterModal({ isOpen, close, loginComplete }) {
  const { style } = useStyle();

  const [mode, setMode] = useState("login");
  const [loading, setLoading] = useState(false);
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showRegisterPassword1, setShowRegisterPassword1] = useState(false);
  const [showRegisterPassword2, setShowRegisterPassword2] = useState(false);

  const [loginData, setLoginData] = useState({
    email: "",
    password: "",
  });

  const [registerData, setRegisterData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    okToEmail: true,
  });

  const emailIsValid = useMemo(() => {
    const email = mode === "login" ? loginData.email : registerData.email;
    if (!email) return true;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  }, [mode, loginData.email, registerData.email]);

  const passwordLengthValid = registerData.password.length >= 6;
  const passwordsMatch =
    registerData.confirmPassword.length === 0
      ? true
      : registerData.password === registerData.confirmPassword;

  const primaryButtonBg = "rgba(120, 150, 190, 0.14)";
  const primaryButtonBorder = "rgba(140, 170, 210, 0.28)";
  const primaryButtonHover = "rgba(120, 150, 190, 0.22)";

  const sharedInputSx = {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    boxSizing: "border-box",

    "& .MuiInputBase-root": {
      color: style.color4,
      backgroundColor: style.darkerPanel || "rgba(6, 9, 14, 0.55)",
      boxShadow: "none",
      borderRadius: "10px",
      width: "100%",
      maxWidth: "100%",
      minWidth: 0,
      minHeight: "46px",
      boxSizing: "border-box",
    },

    "& .MuiOutlinedInput-root": {
      minHeight: "46px",
      "& fieldset": {
        borderColor: "rgba(255,255,255,0.10)",
        borderWidth: "1px",
      },
      "&:hover fieldset": {
        borderColor: "rgba(255,255,255,0.16)",
      },
      "&.Mui-focused fieldset": {
        borderColor: style.color3,
        borderWidth: "1px",
      },
    },

    "& .MuiInputBase-input": {
      color: style.color4,
      fontSize: "0.95rem",
      minWidth: 0,
      boxSizing: "border-box",
      paddingTop: "11px",
      paddingBottom: "11px",
    },

    "& .MuiInputLabel-root": {
      color: style.color3,
    },

    "& .MuiInputLabel-root.Mui-focused": {
      color: style.color4,
    },

    "& .MuiFormHelperText-root": {
      marginLeft: 0,
      marginTop: "3px",
      minHeight: "16px",
      lineHeight: 1.2,
      fontSize: "0.78rem",
    },

    "& input:-webkit-autofill": {
      WebkitBoxShadow: `0 0 0 100px ${
        style.darkerPanel || "rgba(6, 9, 14, 0.55)"
      } inset !important`,
      WebkitTextFillColor: `${style.color4} !important`,
      caretColor: `${style.color4} !important`,
    },
  };

  const handleClose = () => {
    setMode("login");
    setLoading(false);
    close();
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handleLoginChange = (field, value) => {
    setLoginData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleRegisterChange = (field, value) => {
    setRegisterData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const submitLogin = async () => {
    const email = loginData.email.trim();
    const password = loginData.password;

    if (!email) {
      toast.error("Please enter your email address.");
      return;
    }

    if (!emailIsValid) {
      toast.error("Please enter a valid email address.");
      return;
    }

    if (!password) {
      toast.error("Please enter your password.");
      return;
    }

    try {
      setLoading(true);

      const { data } = await axios.post("/login", {
        email,
        password,
      });

      if (data?.error) {
        toast.error(data.error);
        return;
      }

      if (data?.token) {
        localStorage.setItem("token", data.token);
      }

      toast.success("Logged in.");
      await loginComplete?.();
    } catch (error) {
      toast.error(error?.response?.data?.error || "Unable to log in.");
    } finally {
      setLoading(false);
    }
  };

  const submitRegister = async () => {
    const email = registerData.email.trim();
    const { password, confirmPassword, okToEmail } = registerData;

    if (!email) {
      toast.error("Please enter your email address.");
      return;
    }

    if (!emailIsValid) {
      toast.error("Please enter a valid email address.");
      return;
    }

    if (!password) {
      toast.error("Password cannot be blank.");
      return;
    }

    if (password.length < 6) {
      toast.error("Password must be at least 6 characters.");
      return;
    }

    if (!confirmPassword) {
      toast.error("Please confirm your password.");
      return;
    }

    if (password !== confirmPassword) {
      toast.error("Passwords do not match.");
      return;
    }

    try {
      setLoading(true);

      const registerResponse = await axios.post("/register", {
        email,
        password,
        okToEmail,
      });

      if (registerResponse.data?.error) {
        toast.error(registerResponse.data.error);
        return;
      }

      const loginResponse = await axios.post("/login", {
        email,
        password,
      });

      if (loginResponse.data?.error) {
        toast.error(loginResponse.data.error);
        return;
      }

      const userData = loginResponse.data;

      if (userData?.token) {
        localStorage.setItem("token", userData.token);
      }

      toast.success("Account created.");
      await loginComplete?.();
    } catch (error) {
      toast.error(
        error?.response?.data?.error ||
          "Unable to create your account right now."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={Boolean(isOpen)}
      onClose={handleClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{
        sx: {
          backgroundColor: "transparent",
          boxShadow: "none",
          overflow: "visible",
          width: "100%",
          maxWidth: "444px",
          minWidth: 0,
          boxSizing: "border-box",
          mx: { xs: 1.5, sm: "auto" },
        },
      }}
    >
      <DialogContent
        sx={{
          p: 0,
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          overflow: "hidden",
          boxSizing: "border-box",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            width: "100%",
            maxWidth: "100%",
            minWidth: 0,
            overflow: "hidden",
            borderRadius: "10px",
            backgroundColor: style.color1,
            border: `1px solid ${style.color2}`,
            boxShadow: "none",
            boxSizing: "border-box",
          }}
        >
          <Box
            sx={{
              px: { xs: 2, md: 2.5 },
              pt: { xs: 2, md: 2.25 },
              pb: 1.25,
              width: "100%",
              maxWidth: "100%",
              minWidth: 0,
              boxSizing: "border-box",
              overflow: "hidden",
              borderBottom: "1px solid rgba(255,255,255,0.08)",
              backgroundColor: style.darkPanel || "rgba(9, 12, 18, 0.45)",
            }}
          >
            <Stack
              spacing={0.75}
              alignItems="center"
              textAlign="center"
              sx={{
                width: "100%",
                maxWidth: "100%",
                minWidth: 0,
                overflow: "hidden",
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  color: style.color4,
                  fontWeight: 700,
                  letterSpacing: "-0.02em",
                  maxWidth: "100%",
                  wordBreak: "break-word",
                }}
              >
                {mode === "login" ? "Sign In" : "Create Account"}
              </Typography>

            </Stack>
          </Box>

          <Box
            sx={{
              px: { xs: 2, md: 2.5 },
              py: { xs: 1.75, md: 2.15 },
              width: "100%",
              maxWidth: "100%",
              minWidth: 0,
              overflow: "hidden",
              boxSizing: "border-box",
            }}
          >
            {mode === "login" ? (
              <Stack
                spacing={0.9}
                sx={{
                  width: "100%",
                  maxWidth: "100%",
                  minWidth: 0,
                  overflow: "hidden",
                }}
              >
                <TextField
                  label="Email Address"
                  type="email"
                  autoComplete="email"
                  fullWidth
                  value={loginData.email}
                  onChange={(e) => handleLoginChange("email", e.target.value)}
                  error={!!loginData.email && !emailIsValid}
                  helperText={
                    !!loginData.email && !emailIsValid
                      ? "Enter a valid email address."
                      : " "
                  }
                  sx={sharedInputSx}
                />

                <FormControl fullWidth sx={sharedInputSx}>
                  <InputLabel htmlFor="modal-login-password">
                    Password
                  </InputLabel>
                  <OutlinedInput
                    id="modal-login-password"
                    type={showLoginPassword ? "text" : "password"}
                    value={loginData.password}
                    autoComplete="current-password"
                    onChange={(e) =>
                      handleLoginChange("password", e.target.value)
                    }
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() =>
                            setShowLoginPassword((show) => !show)
                          }
                          onMouseDown={handleMouseDownPassword}
                          edge="end"
                          sx={{ color: style.color3 }}
                        >
                          {showLoginPassword ? (
                            <VisibilityOff fontSize="small" />
                          ) : (
                            <Visibility fontSize="small" />
                          )}
                        </IconButton>
                      </InputAdornment>
                    }
                    label="Password"
                  />
                  <FormHelperText sx={{ color: style.color3 }}>
                    {" "}
                  </FormHelperText>
                </FormControl>

                <Button
                  onClick={submitLogin}
                  disabled={loading}
                  startIcon={!loading ? <LoginIcon fontSize="small" /> : null}
                  sx={{
                    width: "100%",
                    maxWidth: "100%",
                    minWidth: 0,
                    minHeight: "42px",
                    boxSizing: "border-box",
                    textTransform: "none",
                    fontWeight: 700,
                    color: style.color4,
                    backgroundColor: primaryButtonBg,
                    borderWidth: "1px",
                    borderColor: primaryButtonBorder,
                    borderStyle: "solid",
                    "&:hover": {
                      backgroundColor: primaryButtonHover,
                      borderColor: "rgba(160, 190, 230, 0.38)",
                    },
                    "&.Mui-disabled": {
                      color: "rgba(255,255,255,0.45)",
                      background: "rgba(255,255,255,0.08)",
                      borderColor: "rgba(255,255,255,0.08)",
                    },
                  }}
                >
                  {loading ? (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <CircularProgress size={16} sx={{ color: style.color4 }} />
                      <span>Signing in...</span>
                    </Stack>
                  ) : (
                    "Sign In"
                  )}
                </Button>

                <Divider
                  sx={{
                    borderColor: "rgba(255,255,255,0.08)",
                    my: 0.35,
                    width: "100%",
                  }}
                />

                <Stack spacing={0.55} alignItems="center" sx={{ width: "100%" }}>
                  <Typography sx={{ color: style.color3, fontSize: "0.9rem" }}>
                    Need an account?
                  </Typography>

                  <Button
                    onClick={() => setMode("register")}
                    startIcon={<PersonAddAltRoundedIcon fontSize="small" />}
                    sx={{
                      px: 2,
                      py: 0.75,
                      maxWidth: "100%",
                      minWidth: 0,
                      boxSizing: "border-box",
                      textTransform: "none",
                      fontWeight: 700,
                      fontSize: "0.9rem",
                      color: style.color4,
                      backgroundColor:
                        style.darkPanel || "rgba(9, 12, 18, 0.45)",
                      border: "1px solid rgba(255,255,255,0.08)",
                      "&:hover": {
                        backgroundColor:
                          style.darkerPanel || "rgba(6, 9, 14, 0.55)",
                      },
                    }}
                  >
                    Create Account
                  </Button>
                </Stack>
              </Stack>
            ) : (
              <Stack
                spacing={0.8}
                sx={{
                  width: "100%",
                  maxWidth: "100%",
                  minWidth: 0,
                  overflow: "hidden",
                }}
              >
                <TextField
                  label="Email Address"
                  type="email"
                  autoComplete="email"
                  fullWidth
                  value={registerData.email}
                  onChange={(e) =>
                    handleRegisterChange("email", e.target.value)
                  }
                  error={!!registerData.email && !emailIsValid}
                  helperText={
                    !!registerData.email && !emailIsValid
                      ? "Enter a valid email address."
                      : " "
                  }
                  sx={sharedInputSx}
                />

                <FormControl fullWidth sx={sharedInputSx}>
                  <InputLabel htmlFor="modal-register-password">
                    Password
                  </InputLabel>
                  <OutlinedInput
                    id="modal-register-password"
                    type={showRegisterPassword1 ? "text" : "password"}
                    value={registerData.password}
                    autoComplete="new-password"
                    onChange={(e) =>
                      handleRegisterChange("password", e.target.value)
                    }
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() =>
                            setShowRegisterPassword1((show) => !show)
                          }
                          onMouseDown={handleMouseDownPassword}
                          edge="end"
                          sx={{ color: style.color3 }}
                        >
                          {showRegisterPassword1 ? (
                            <VisibilityOff fontSize="small" />
                          ) : (
                            <Visibility fontSize="small" />
                          )}
                        </IconButton>
                      </InputAdornment>
                    }
                    label="Password"
                  />
                  <FormHelperText
                    sx={{
                      color: !registerData.password
                        ? style.color3
                        : passwordLengthValid
                        ? "#66bb6a"
                        : "#ef5350",
                    }}
                  >
                    {!registerData.password
                      ? " "
                      : passwordLengthValid
                      ? "Password looks good"
                      : "Minimum 6 characters"}
                  </FormHelperText>
                </FormControl>

                <FormControl fullWidth sx={sharedInputSx}>
                  <InputLabel htmlFor="modal-register-confirm-password">
                    Confirm Password
                  </InputLabel>
                  <OutlinedInput
                    id="modal-register-confirm-password"
                    type={showRegisterPassword2 ? "text" : "password"}
                    value={registerData.confirmPassword}
                    autoComplete="new-password"
                    onChange={(e) =>
                      handleRegisterChange("confirmPassword", e.target.value)
                    }
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle confirm password visibility"
                          onClick={() =>
                            setShowRegisterPassword2((show) => !show)
                          }
                          onMouseDown={handleMouseDownPassword}
                          edge="end"
                          sx={{ color: style.color3 }}
                        >
                          {showRegisterPassword2 ? (
                            <VisibilityOff fontSize="small" />
                          ) : (
                            <Visibility fontSize="small" />
                          )}
                        </IconButton>
                      </InputAdornment>
                    }
                    label="Confirm Password"
                  />
                  <FormHelperText
                    sx={{
                      color: !registerData.confirmPassword
                        ? style.color3
                        : passwordsMatch
                        ? "#66bb6a"
                        : "#ef5350",
                    }}
                  >
                    {!registerData.confirmPassword
                      ? " "
                      : passwordsMatch
                      ? "Passwords match"
                      : "Passwords do not match"}
                  </FormHelperText>
                </FormControl>

                <Box
                  sx={{
                    px: 1,
                    py: 0.25,
                    width: "100%",
                    maxWidth: "100%",
                    minWidth: 0,
                    boxSizing: "border-box",
                    overflow: "hidden",
                    backgroundColor:
                      style.darkPanel || "rgba(9, 12, 18, 0.45)",
                    border: "1px solid rgba(255,255,255,0.06)",
                    borderRadius: "10px",
                  }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={registerData.okToEmail}
                        onChange={(e) =>
                          handleRegisterChange("okToEmail", e.target.checked)
                        }
                        size="small"
                        sx={{
                          color: style.color3,
                          py: 0.5,
                          "&.Mui-checked": {
                            color: style.color4,
                          },
                        }}
                      />
                    }
                    label={
                      <Typography
                        sx={{
                          color: style.color3,
                          fontSize: "0.88rem",
                          wordBreak: "break-word",
                        }}
                      >
                        Email me updates and offers
                      </Typography>
                    }
                    sx={{
                      my: 0,
                      mr: 0,
                      width: "100%",
                      maxWidth: "100%",
                      minWidth: 0,
                      alignItems: "flex-start",
                    }}
                  />
                </Box>

                <Button
                  onClick={submitRegister}
                  disabled={loading}
                  sx={{
                    width: "100%",
                    maxWidth: "100%",
                    minWidth: 0,
                    minHeight: "42px",
                    boxSizing: "border-box",
                    textTransform: "none",
                    fontWeight: 700,
                    color: style.color4,
                    backgroundColor: primaryButtonBg,
                    borderWidth: "1px",
                    borderColor: primaryButtonBorder,
                    borderStyle: "solid",
                    "&:hover": {
                      backgroundColor: primaryButtonHover,
                      borderColor: "rgba(160, 190, 230, 0.38)",
                    },
                    "&.Mui-disabled": {
                      color: "rgba(255,255,255,0.45)",
                      background: "rgba(255,255,255,0.08)",
                      borderColor: "rgba(255,255,255,0.08)",
                    },
                  }}
                >
                  {loading ? (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <CircularProgress size={16} sx={{ color: style.color4 }} />
                      <span>Creating account...</span>
                    </Stack>
                  ) : (
                    "Create Account"
                  )}
                </Button>

                <Divider
                  sx={{
                    borderColor: "rgba(255,255,255,0.08)",
                    my: 0.25,
                    width: "100%",
                  }}
                />

                <Stack spacing={0.45} alignItems="center" sx={{ width: "100%" }}>
                  <Typography sx={{ color: style.color3, fontSize: "0.88rem" }}>
                    Already have an account?
                  </Typography>

                  <Button
                    onClick={() => setMode("login")}
                    startIcon={<LoginIcon fontSize="small" />}
                    sx={{
                      px: 2,
                      py: 0.7,
                      maxWidth: "100%",
                      minWidth: 0,
                      boxSizing: "border-box",
                      textTransform: "none",
                      fontWeight: 700,
                      fontSize: "0.9rem",
                      color: style.color4,
                      backgroundColor:
                        style.darkPanel || "rgba(9, 12, 18, 0.45)",
                      border: "1px solid rgba(255,255,255,0.08)",
                      "&:hover": {
                        backgroundColor:
                          style.darkerPanel || "rgba(6, 9, 14, 0.55)",
                      },
                    }}
                  >
                    Sign In
                  </Button>
                </Stack>

                <Box sx={{ pt: 0.15, textAlign: "center", maxWidth: "100%" }}>
                  <Typography
                    sx={{
                      color: style.color3,
                      fontSize: "0.8rem",
                      lineHeight: 1.35,
                      wordBreak: "break-word",
                    }}
                  >
                    By creating an account, you agree to our terms and
                    conditions.
                  </Typography>

                  <Button
                    href="/terms"
                    sx={{
                      mt: 0,
                      minWidth: 0,
                      maxWidth: "100%",
                      p: 0.1,
                      textTransform: "none",
                      fontWeight: 700,
                      fontSize: "0.8rem",
                      color: style.color4,
                      "&:hover": {
                        backgroundColor: "transparent",
                        color: style.color3,
                      },
                    }}
                  >
                    View terms and conditions
                  </Button>
                </Box>
              </Stack>
            )}

            <Box
              sx={{
                pt: mode === "login" ? 1 : 0.4,
                textAlign: "center",
                width: "100%",
                maxWidth: "100%",
                minWidth: 0,
              }}
            >
              <Button
                onClick={handleClose}
                sx={{
                  textTransform: "none",
                  color: style.color3,
                  fontWeight: 700,
                  maxWidth: "100%",
                  minWidth: 0,
                  py: 0.35,
                  "&:hover": {
                    backgroundColor: "transparent",
                    color: style.color4,
                  },
                }}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        </Paper>
      </DialogContent>
    </Dialog>
  );
}